import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import ChecklistIcon from '@mui/icons-material/Checklist';

import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import MuiPhoneNumber from 'mui-phone-number';
import { MuiTelInput } from 'mui-tel-input';
import React from 'react';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { emptyPickupTask } from '../../../../data/pickuptasks';
import { DeliveryState, Workflow } from '../../../../enums/orders';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { emptyCustomer } from '../../../../data/customers';
import { CustomerType } from '../../../../interfaces/common/customer';
import GenericTable from '../../../common/generic-table/generic-table';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import { useImmer } from 'use-immer';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import { showDeliveryState } from '../../../common/styles/order-states.style';
import {
  displayLocation,
  displayMultipleLocations,
} from '../../../../utils/locations';
import { ILocation } from '../../../../interfaces/common/location';
import { isMobile, isTablet } from 'react-device-detect';
import { useSnackBar } from '../../../../contexts/snackbar';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { DownloadIcon } from 'lucide-react';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import Grid2 from '@mui/material/Unstable_Grid2';
import MoneyInput from '../../../common/money-input/money-input';

import { add, displayMoney } from '../../../../utils/money';
import { compute_total_pop } from '../../../common/report-builder/utils';
import { showWorkflow } from '../../../common/styles/workflow-display';
import IncompleteAddressError from '../../../common/incomplete-address-error/incomplete-address-error';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { useVideoTutorial } from '../../../../contexts/video-tutorial-context/video-tutorial-context';
import HelpIcon from '@mui/icons-material/Help';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import NewFeatureChip from '../../../common/new-feature-chip';

interface MerchantCreatePickupRequestProps {
  onSuccess: (p: IPickupTask, selectedOrders: OrderType[], is_newly_created: boolean) => void;
}

function MerchantCreatePickupRequest({
  onSuccess,
}: MerchantCreatePickupRequestProps) {
  const [taskData, setTaskData] = useState<IPickupTask>(emptyPickupTask);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const { settings } = useContext(OrgSettingsContext);
  const { showVideoTutorial } = useVideoTutorial();

  const { showMessage } = useSnackBar();

  const { user, config } = useContext(AuthContext);

  const [selectedOrders, updateSelectedOrders] = useImmer<
    Map<string, OrderType>
  >(new Map());

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const totalSelected = selectedOrders.size;

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  useEffect(() => {
    setTaskData({ ...emptyPickupTask, workflow: taskData.workflow });
  }, [taskData.workflow]);

  useEffect(() => {
    const getAndSetOrders = async () => {
      setLoadingOrders(true);
      if (
        taskData.workflow === Workflow.STANDARD &&
        taskData.merchant_location == null
      ) {
        setOrders([]);
        setLoadingOrders(false);
        return;
      }

      if (
        taskData.workflow === Workflow.RETURN && taskData.customer == null
      ) {
        setOrders([]);
        setLoadingOrders(false);
        return;
      }

      const relevant_fields = [
        'id',
        'order_id',
        'reference_id',
        'creation_date',
        'customer',
        'customer_location',
        'governorate',
        'price',
        'merchant',
        'labels',
        'delivery_fee',
        'number_of_packages',
        'delivery_state',
        'pop',
        'payment_state',
        'tracking_hash',
        'public_note',
        'workflow',
        'public_note',
        'description',
        'merchant_location',
        'return_reason',
        'exchange_for',
      ];

      let endpoint = `${API_ENDPOINT}/api/v1/orders/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      if (controller.searchColumn !== '' && controller.searchValue !== '') {
        endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${encodeURIComponent(controller.searchValue)}`;
      }

      endpoint += `&delivery_state=${DeliveryState.CREATED}`;
      endpoint += `&workflow=${taskData.workflow}`;

      if (taskData.workflow === Workflow.RETURN)
        endpoint += `&customer=${taskData.customer?.id}`;
      else endpoint += `&merchant_location=${taskData.merchant_location?.id}`;

      try {
        console.log(endpoint);
        const response = await axios.get<{
          count: number;
          results: OrderType[];
        }>(endpoint, config);
        setOrders(
          response.data.results.map((order) => {
            return {
              ...order,
              merchant_location: taskData.merchant_location,
            };
          })
        );
        setOrdersCount(response.data.count);
      } catch (error) {
        console.log(error);
        setOrders([]);
      }
      setLoadingOrders(false);
    };

    getAndSetOrders();
  }, [
    taskData.workflow,
    taskData.customer,
    taskData.merchant_location,
    taskData.customer_location,
    JSON.stringify(controller),
  ]);

  const handleFetchCustomers = async (
    query: string
  ): Promise<CustomerType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/customers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: CustomerType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const handleFetchPickupLocations = async (query: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page_size=10&name__contains=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: ShopLocationType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }
    return [];
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (taskData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving pickup location...');

      let requestData: any = {
        workflow: taskData.workflow,
        merchant_location: taskData.merchant_location,
        customer_location: taskData.customer_location,
        merchant: taskData.merchant,
        orders: Array.from(selectedOrders.values()),
      };

      if (taskData.workflow === Workflow.RETURN) {
        requestData = {
          ...requestData,
          customer: taskData.customer,
        };
      }

      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/v1/pickup-tasks/`,
          requestData,
          config
        );

        setResponse(response);
        if (response.status >= 200 && response.status < 300) {
          setMessage('Pickup requested successfully');
        }

        onSuccess && onSuccess(response.data, Array.from(selectedOrders.values()), response.data.was_created);

      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage(
            'Failed to save pickup request: ' + e.response?.statusText
          );
        } else {
          setMessage('Failed to save pickup request');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        New Pickup Request 
      </Typography>

      
      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {taskData && (
            <>
              <br />
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <FormLabel id="demo-radio-buttons-group-label">
                    Workflow
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={taskData.workflow}
                    row
                    name="radio-buttons-group"
                    onChange={(e) => {
                      setTaskData({
                        ...taskData,
                        workflow: e.target.value as Workflow,
                      });
                    }}
                  >
                    <FormControlLabel
                      value={Workflow.STANDARD}
                      control={<Radio />}
                      label="Standard"
                    />
                    <FormControlLabel
                      value={Workflow.RETURN}
                      control={<Radio />}
                      label="Return"
                    />
                  </RadioGroup>
                </FormControl>
                <br />
                <br />
                {taskData.workflow === Workflow.STANDARD ? (
                  <AutocompleteWithThrottle
                    label={'Pickup Location'}
                    fetchAllOnLoad
                    key="merchant-pickup"
                    value={taskData.merchant_location}
                    onChange={(e, value) =>
                      setTaskData({ ...taskData, merchant_location: value })
                    }
                    fetch={handleFetchPickupLocations}
                    getOptionLabel={function (
                      option: ShopLocationType
                    ): string {
                      return `${option.name}: ${displayLocation(option)}`;
                    }}
                  />
                ) : (
                  <>
                    <AutocompleteWithThrottle
                      label={'Customer'}
                      key="customer"
                      fetch={handleFetchCustomers}
                      value={taskData.customer}
                      placeholder="Search by name or phone number"
                      getOptionLabel={(option: CustomerType) =>
                        option.name + ' ' + option.phone_number
                      }
                      onChange={(e: any, value: CustomerType | null) => {
                        setTaskData({ ...taskData, customer: value });
                      }}
                    />
                  </>
                )}
                <br />
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Total Pay on Pickup:{' '}
                  {displayMoney(
                    add(
                      compute_total_pop(Array.from(selectedOrders.values())),
                      taskData.pop
                    )
                  )}
                </Typography>
                <Chip
                  icon={<ChecklistIcon />}
                  color="primary"
                  label={`Selected ${totalSelected}/${ordersCount}`}
                  variant={
                    totalSelected === ordersCount && totalSelected > 0
                      ? 'filled'
                      : 'outlined'
                  }
                />
                <br /> <br />
                <GenericTable
                  sourceView="merchant-create-task-form"
                  items={orders}
                  selectedItems={selectedOrders}
                  updateSelectedItems={updateSelectedOrders}
                  isLoading={loadingOrders}
                  fullWidth
                  selectionEnabled
                  columns={[
                    {
                      id: 'order_id',
                      header: 'Order ID',
                      display_body: (order) => <>{order.order_id}</>,
                    },
                    {
                      id: 'packages',
                      header: '# Packages',
                      display_body: (order) => <>{order.number_of_packages}</>,
                    },
                    {
                      id: 'customer',
                      header: 'Customer',
                      display_body: (order) => (
                        <>
                          {order.customer?.name}
                          <br /> {displayLocation(order.customer_location)}
                          {!order.customer_location?.area && (
                            <IncompleteAddressError />
                          )}
                        </>
                      ),
                    },
                    {
                      id: 'pop',
                      header: 'Pay on Pickup',
                      display_body: (order) => displayMoney(order.pop),
                    },
                    {
                      id: 'workflow',
                      header: 'Workflow',
                      display_body: (order) =>
                        showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason),
                    },
                    {
                      id: 'delivery state',
                      header: 'Status',
                      display_body: (order) =>
                        showDeliveryState(order.delivery_state),
                    },
                  ]}
                  getItemUID={(order) => order.id}
                />
                <TablePagination
                  component="div"
                  onPageChange={handlePageChange}
                  page={controller.page}
                  count={ordersCount}
                  rowsPerPage={controller.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                  labelRowsPerPage=""
                  style={{ overflow: 'visible' }}
                />
                <br />
                {totalSelected === 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      display: 'block',
                      mb: 1,
                      color: 'warning.main',
                    }}
                  >
                    Please select at least one order.
                  </Typography>
                )}
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    variant="contained"
                    type={'submit'}
                    disabled={totalSelected === 0}
                  >
                    Save
                  </Button>
                </div>
              </form>
            </>
          )}
        </>
      )}

      
    </>
  );
}

export default MerchantCreatePickupRequest;
